import type { FC, KeyboardEvent } from 'react';
import { getButtonClassNames } from '../Button/Button.styles';
import {
  getAccessibleTabIndex,
  getAriaButtonRoleAttribute,
  handleAccessibleButtonKeyDown,
} from '../Button/ariaHelpers';
import { Icon } from '../Icon/Icon';
import { useConditionalClickHandler } from '../hooks/useConditionalClickHandler';
import { fromRenderAsToElement } from '../utils/InteractiveAtomProps';
import {
  type IconButtonProps,
  fromIconButtonToIconSize,
} from './IconButton.types';

export const IconButton: FC<IconButtonProps> = ({
  icon,
  iconProps,
  renderAs = 'button',
  accent = 'positive',
  disabled,
  pressed,
  className,
  size = 'default',
  variant,
  onClick,
  loading,
  elementProps,
  type,
  forwardRef,
  dataTestId,
  ...restProps
}) => {
  const DynamicComponent: any = fromRenderAsToElement[renderAs];

  const buttonType = renderAs === 'button' ? type || 'button' : undefined;
  const buttonDisabled = loading || disabled;

  const handleClick = useConditionalClickHandler<
    HTMLButtonElement | HTMLAnchorElement
  >(buttonDisabled, onClick);

  return (
    <DynamicComponent
      {...elementProps}
      {...restProps}
      ref={forwardRef}
      data-testid={dataTestId}
      className={getButtonClassNames(
        variant,
        className,
        accent,
        size,
        buttonDisabled,
        pressed,
      )}
      disabled={buttonDisabled}
      type={buttonType}
      onClick={handleClick}
      {...getAriaButtonRoleAttribute(renderAs !== 'button')}
      tabIndex={getAccessibleTabIndex(buttonDisabled)}
      onKeyDown={(
        e: KeyboardEvent<HTMLButtonElement> | KeyboardEvent<HTMLAnchorElement>,
      ) => {
        if (renderAs !== 'button') {
          handleAccessibleButtonKeyDown(e, buttonDisabled);
        }
      }}
    >
      <Icon
        glyph={icon}
        loading={loading}
        size={iconProps?.size || fromIconButtonToIconSize[size]}
        {...iconProps}
      />
    </DynamicComponent>
  );
};

IconButton.displayName = 'IconButton';
